import { createContext, useCallback, useState, ReactNode } from "react";

interface NotificationType {
  type: string;
  message?: string;
  [key: string]: unknown;
}

interface NotificationContextType {
  notifications: NotificationType[];
  addNotification: (notification: NotificationType) => void;
  removeNotification: (notification: NotificationType) => void;
  clearNotifications: () => void;
}

const defaultContextValue: NotificationContextType = {
  notifications: [],
  addNotification: () => {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        "addNotification was called outside of NotificationProvider",
      );
    }
  },
  removeNotification: () => {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        "removeNotification was called outside of NotificationProvider",
      );
    }
  },
  clearNotifications: () => {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        "clearNotifications was called outside of NotificationProvider",
      );
    }
  },
};

export const NotificationContext =
  createContext<NotificationContextType>(defaultContextValue);

interface NotificationProviderProps {
  children: ReactNode;
}

function NotificationContextProvider({ children }: NotificationProviderProps) {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [isForceCleared, setIsForceCleared] = useState(false);

  // Add new notification
  const addNotification = useCallback(
    (notification: NotificationType) => {
      if (isForceCleared) return;

      setNotifications((prevNotifications) => {
        const index = prevNotifications.findIndex(
          (n) => n.type === notification.type,
        );
        if (index === -1) {
          return [...prevNotifications, notification];
        }
        return prevNotifications;
      });
    },
    [isForceCleared],
  );

  // Remove a notification
  const removeNotification = useCallback((notification: NotificationType) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((n) => n.type !== notification.type),
    );
  }, []);

  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setIsForceCleared(true);
  }, []);

  const contextData: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={contextData}>
      {children}
    </NotificationContext.Provider>
  );
}

export { NotificationContextProvider };
